export const expertsHero = {
  badge: "On-Demand Expertise",
  title: "TIE Experts' Platform & Turn-key Consultancy",
  subtitle:
    "A curated marketplace of 150+ textile specialists covering lean manufacturing, dyeing, compliance, digital systems, and sustainability. Factories engage experts for turn-key projects, diagnostic audits, or ongoing advisory retainers — matched within 48 hours.",
  ctaPrimary: "Find an Expert",
  ctaSecondary: "Explore Domains",
};

export const expertsWhatIs = {
  title: "What is the Experts' Platform?",
  paragraphs: [
    "The TIE Experts' Platform is a curated, vetted marketplace connecting textile and apparel factories with specialized practitioners who have deep, hands-on experience in specific production domains. Unlike general consulting firms, every expert on the platform has been assessed for both technical depth and factory-floor practicality.",
    "Factories can engage experts in three modes: turn-key project delivery, where the expert designs and implements a solution end-to-end; diagnostic audits, where the expert evaluates a specific process or system and delivers a findings report with recommendations; and advisory retainers, where the expert provides ongoing guidance to internal teams over a defined period.",
    "The platform is integrated with TIE's other programmes. Experts who work on PIIC or PTIM deployments are automatically profiled on the platform. Factories that complete Training programmes can use the platform to find mentors for their certified practitioners. The result is an ecosystem where knowledge flows continuously.",
  ],
  highlights: [
    { label: "Experts on Platform", value: "150+" },
    { label: "Specializations", value: "30+" },
    { label: "Match Time", value: "48h" },
    { label: "Factory Projects", value: "200+" },
  ],
};

export const expertDomains = [
  {
    name: "Lean Manufacturing",
    icon: "ri-speed-line",
    description:
      "Waste elimination, value stream mapping, 5S implementation, kanban systems, and continuous flow optimization for sewing and finishing operations.",
    experts: "28",
    projects: "45+",
  },
  {
    name: "Dyeing & Chemical Processing",
    icon: "ri-drop-line",
    description:
      "Process optimization, water and chemical reduction, right-first-time improvement, shade matching, and sustainable processing protocol development.",
    experts: "22",
    projects: "38+",
  },
  {
    name: "Compliance & Sustainability",
    icon: "ri-shield-check-line",
    description:
      "Social compliance auditing, environmental certification support, ESG reporting, carbon footprint analysis, and buyer standard alignment.",
    experts: "18",
    projects: "32+",
  },
  {
    name: "Digital Systems & Automation",
    icon: "ri-cpu-line",
    description:
      "ERP implementation, IoT sensor deployment, real-time production monitoring, AI-driven quality inspection, and manufacturing execution systems.",
    experts: "15",
    projects: "28+",
  },
  {
    name: "Quality Management",
    icon: "ri-award-line",
    description:
      "RFT improvement, defect root-cause analysis, statistical process control, inspection system design, and buyer quality standard optimization.",
    experts: "20",
    projects: "35+",
  },
  {
    name: "Product Development & Materials",
    icon: "ri-stack-line",
    description:
      "New fiber evaluation, blended yarn development, technical textile design, material testing protocols, and MMF transition strategy.",
    experts: "16",
    projects: "24+",
  },
  {
    name: "Energy & Resource Efficiency",
    icon: "ri-flashlight-line",
    description:
      "Energy audit and retrofit planning, steam system optimization, compressed air efficiency, renewable energy feasibility, and utility cost reduction.",
    experts: "14",
    projects: "22+",
  },
  {
    name: "Supply Chain & Logistics",
    icon: "ri-truck-line",
    description:
      "Lead-time compression, inventory optimization, vendor management, freight consolidation, and end-to-end supply chain digitization.",
    experts: "17",
    projects: "26+",
  },
];

export const expertsProcessSteps = [
  {
    phase: "Step 1",
    week: "Day 1",
    title: "Need Assessment & Expert Matching",
    description:
      "The factory submits a brief describing the problem, desired outcome, timeline, and budget. TIE's matching algorithm and programme directors identify 2-3 qualified experts within 48 hours.",
    activities: [
      "Factory submits detailed project brief",
      "TIE assesses scope and complexity",
      "Algorithm + director matching of 2-3 candidates",
      "Expert profiles and track records shared",
    ],
  },
  {
    phase: "Step 2",
    week: "Days 2-3",
    title: "Interview & Scope Finalization",
    description:
      "The factory interviews matched experts via video call or factory visit. The selected expert and factory agree on scope, deliverables, timeline, and fee structure. TIE facilitates contract signing.",
    activities: [
      "Factory interviews shortlisted experts",
      "Scope, KPIs, and deliverables defined",
      "Fee and payment terms agreed",
      "TIE-facilitated contract execution",
    ],
  },
  {
    phase: "Step 3",
    week: "Project Duration",
    title: "Execution & Milestone Tracking",
    description:
      "The expert works on-site or remotely depending on the engagement model. TIE programme directors provide oversight, milestone reviews, and conflict resolution if needed. Progress is documented in real time.",
    activities: [
      "On-site or remote expert deployment",
      "Weekly milestone review with TIE",
      "Real-time progress documentation",
      "Mid-project adjustment if required",
    ],
  },
  {
    phase: "Step 4",
    week: "Completion",
    title: "Delivery, Validation & Knowledge Transfer",
    description:
      "The expert delivers final outputs — reports, SOPs, implemented systems, or trained teams. TIE validates deliverables against the agreed scope. A knowledge transfer session ensures the factory can sustain results independently.",
    activities: [
      "Final deliverables submission",
      "TIE validation against agreed scope",
      "Knowledge transfer session to internal team",
      "Project closure and expert rating",
    ],
  },
];

export const expertsOutcomes = {
  title: "What Every Engagement Delivers",
  items: [
    {
      title: "Matched in 48 Hours",
      description:
        "No lengthy procurement cycles. TIE's matching system delivers vetted expert candidates within two business days of receiving the factory brief.",
      icon: "ri-timer-flash-line",
    },
    {
      title: "Defined Scope & Deliverables",
      description:
        "Every engagement starts with a clear statement of work, measurable KPIs, and a timeline — protecting both the factory and the expert from scope creep.",
      icon: "ri-file-list-3-line",
    },
    {
      title: "Knowledge Transfer Built In",
      description:
        "Experts are required to transfer skills to internal staff during the engagement. The factory gains capability, not just a one-time fix.",
      icon: "ri-presentation-line",
    },
    {
      title: "TIE Oversight & Quality Assurance",
      description:
        "Programme directors monitor every engagement, conduct milestone reviews, and intervene if deliverables fall below the agreed standard.",
      icon: "ri-shield-check-line",
    },
    {
      title: "Verified Expert Track Records",
      description:
        "Every expert profile includes past project outcomes, factory ratings, and specialization depth — so factories hire with confidence.",
      icon: "ri-bar-chart-grouped-line",
    },
    {
      title: "Flexible Engagement Models",
      description:
        "Turn-key projects, diagnostic audits, advisory retainers, or training workshops — the platform adapts to what the factory actually needs.",
      icon: "ri-exchange-line",
    },
  ],
};

export const expertsStats = [
  { value: "150+", label: "Vetted Experts", sub: "across 30+ specializations" },
  { value: "48h", label: "Average Match Time", sub: "from brief to shortlist" },
  { value: "200+", label: "Projects Delivered", sub: "since platform launch" },
  { value: "94%", label: "Satisfaction Rate", sub: "factories rate engagement highly" },
];